import {
  downloadFileName,
  foldSearchText,
  REVISION_STATES,
  type RevisionState,
  revisionStateLabel,
  type ShipTime,
} from '@felix/domain';
import type { MenuScope } from '../../menu.js';
import type { StatusTone } from '../../StatusBadge.js';
import { zoneOffsetMinutes } from '../personnel/rest-hours.js';
import { OTHER_GROUP_ID, otherDocumentIds, RETIRED_GROUP_ID } from './folder-tree.js';
import type {
  LibraryDetail,
  LibraryDocument,
  LibraryFolder,
  LibraryRevision,
  LibrarySection,
  LibraryTree,
} from './types.js';

/**
 * Doküman kütüphanesi ekranlarının saf kuralları (plan M8a Task 14–15): ağaç, arama, klavye,
 * rozet tonu, boyut, önizlenen revizyon ve basılı kopya altlığı. Ekran bileşenleri yalnız
 * bunları çağırır; testleri I/O'suzdur.
 */

/**
 * Bölümün adresi ve sorgusu: ISM Forms ve Manuals menü sayfalarıdır; PMS Instructions
 * Maintenance Plan'ın sayfa içi sekmesidir (`/pms/plan?tab=instructions`; K38).
 */
export function sectionHref(
  section: LibrarySection,
  params: Readonly<Record<string, string | null | undefined>> = {},
): string {
  const base =
    section === 'ism'
      ? '/compliance/ism-forms'
      : section === 'manuals'
        ? '/compliance/manuals'
        : '/pms/plan';
  const query = new URLSearchParams(section === 'pms_instructions' ? { tab: 'instructions' } : {});
  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, value);
  }
  const text = query.toString();
  return text ? `${base}?${text}` : base;
}

export type LibraryNode =
  | { kind: 'folder'; id: string; folder: LibraryFolder; children: LibraryNode[] }
  | { kind: 'document'; id: string; document: LibraryDocument };

const byOrder = <T extends { sortOrder: number }>(key: (item: T) => string) => {
  const collator = new Intl.Collator('en', { numeric: true });
  return (a: T, b: T) => a.sortOrder - b.sortOrder || collator.compare(key(a), key(b));
};

/**
 * Klasör içinde belge sırası (ortağın geri bildirimi 2026-10-17; ADR 0069): "order" sıra ve
 * numara (varsayılan), "date" yürürlük ya da yayın günü, yeniden eskiye. Klasör sırası değişmez.
 */
export type TreeSort = 'order' | 'date';
export const TREE_SORTS: readonly TreeSort[] = ['order', 'date'];
const TREE_SORT_KEY = 'felix.documents.treeSort';

/** Ağaçta belgenin tarihi: yürürlükteki revizyonun yürürlük günü, yoksa yayın günü. */
export function treeDocumentDate(document: Pick<LibraryDocument, 'current'>): string | null {
  const current = document.current;
  if (!current) return null;
  return current.effectiveOn ?? current.publishedAt?.slice(0, 10) ?? null;
}

const byDate = (a: LibraryDocument, b: LibraryDocument): number => {
  const [x, y] = [treeDocumentDate(a), treeDocumentDate(b)];
  if (x !== y) {
    if (x === null) return 1;
    if (y === null) return -1;
    return x < y ? 1 : -1;
  }
  return byOrder<LibraryDocument>((d) => d.docNo)(a, b);
};

/** Kişinin son sıralama seçimi (bu cihazda); depo kapalı ya da değer bilinmiyorsa "order". */
export function recallTreeSort(): TreeSort {
  try {
    const value = globalThis.localStorage?.getItem(TREE_SORT_KEY);
    return value === 'date' ? 'date' : 'order';
  } catch {
    return 'order';
  }
}

export function rememberTreeSort(sort: TreeSort): void {
  try {
    globalThis.localStorage?.setItem(TREE_SORT_KEY, sort);
  } catch {
    // Depo kapalı: seçim bu oturumda kalır
  }
}

/**
 * Klasör ağacı: klasörler sıra ve koda, dokümanlar sıra ve numaraya göre; alt klasörler
 * dokümanlardan önce. Üstü bulunmayan klasör köke düşer (silinmiş üst; kayıt kaybolmaz).
 *
 * Gösterim grupları (ADR 0038 §3, §9), bölümün sonunda:
 * - "Retired folders": emekli klasörler (`retired`), alt ağaçlarıyla ve salt okunur;
 * - "Other company documents": klasörü bu ağaçta olmayan belgeler. Hiçbir belge kaybolmaz.
 * Gruplar `group` alanlı yapay klasör düğümleridir; adlarını ekran çevirir.
 */
export function buildTree(tree: LibraryTree, sort: TreeSort = 'order'): LibraryNode[] {
  const other = otherDocumentIds(tree);
  const documentOrder = sort === 'date' ? byDate : byOrder<LibraryDocument>((d) => d.docNo);
  const documents = [...tree.documents].filter((d) => !other.has(d.id)).sort(documentOrder);
  const build = (
    pool: readonly LibraryFolder[],
    parentId: string | null,
    seen: Set<string>,
  ): LibraryNode[] => {
    const ids = new Set(pool.map((f) => f.id));
    return [
      ...pool
        .filter((f) =>
          parentId === null ? f.parentId === null || !ids.has(f.parentId) : f.parentId === parentId,
        )
        .filter((f) => !seen.has(f.id))
        .map((f): LibraryNode => {
          const next = new Set(seen).add(f.id);
          return { kind: 'folder', id: f.id, folder: f, children: build(pool, f.id, next) };
        }),
      ...(parentId === null
        ? []
        : documents
            .filter((d) => d.folderId === parentId)
            .map((d): LibraryNode => ({ kind: 'document', id: d.id, document: d }))),
    ];
  };
  const sorted = [...tree.folders].sort(byOrder((f) => f.code));
  const live = sorted.filter((f) => !f.retired);
  const retired = sorted.filter((f) => f.retired);
  const nodes = build(live, null, new Set());
  const group = (id: string, kind: 'retired' | 'other', children: LibraryNode[]): LibraryNode => ({
    kind: 'folder',
    id,
    folder: {
      id,
      parentId: null,
      section: tree.section,
      code: '',
      name: '',
      sortOrder: Number.MAX_SAFE_INTEGER,
      rowVersion: 0,
      group: kind,
    },
    children,
  });
  if (retired.length > 0)
    nodes.push(group(RETIRED_GROUP_ID, 'retired', build(retired, null, new Set())));
  const others = tree.documents
    .filter((d) => other.has(d.id))
    .sort(documentOrder)
    .map((d): LibraryNode => ({ kind: 'document', id: d.id, document: d }));
  if (others.length > 0) nodes.push(group(OTHER_GROUP_ID, 'other', others));
  return nodes;
}

/** Ağaç sırasıyla ilk doküman (adreste seçim yoksa açılan). */
export function firstDocumentId(nodes: readonly LibraryNode[]): string | null {
  for (const node of nodes) {
    if (node.kind === 'document') return node.id;
    const inner = firstDocumentId(node.children);
    if (inner) return inner;
  }
  return null;
}

/** "01. Safety Management System" (gemi-ism-formlari.jpeg Form Tree). */
export function folderLabel(folder: Pick<LibraryFolder, 'code' | 'name'>): string {
  return `${folder.code}. ${folder.name}`;
}

/** Ağaçtaki satır: "01 - Safety Management System". */
export function treeLabel(document: Pick<LibraryDocument, 'docNo' | 'title'>): string {
  return `${document.docNo} - ${document.title}`;
}

/** Başlık kartı ve breadcrumb: formda "Form 01 - …", diğerlerinde "01 - …". */
export function documentHeading(
  document: Pick<LibraryDocument, 'kind' | 'docNo' | 'title'>,
): string {
  return document.kind === 'form'
    ? `Form ${document.docNo} - ${document.title}`
    : treeLabel(document);
}

function documentText(document: LibraryDocument): string {
  return [document.docNo, document.title, treeLabel(document), document.description ?? ''].join(
    '\n',
  );
}

/**
 * Form Tree süzgeci [sorular #37]: doküman no, başlık ve açıklamada, klasör kodu ve adında alt
 * dize; Türkçe katlama (`foldSearchText`). Dosya içinde aranmaz. Eşleşen dokümanın klasörleri
 * açılır; adı eşleşen klasörün bütün içeriği görünür.
 */
export function filterTree(
  nodes: readonly LibraryNode[],
  query: string,
): { nodes: LibraryNode[]; expand: Set<string> } {
  const needle = foldSearchText(query.trim());
  const expand = new Set<string>();
  if (needle === '') return { nodes: [...nodes], expand };
  const has = (text: string) => foldSearchText(text).includes(needle);
  // Adı eşleşen klasörün alt klasörleri de açılır: içerikleri aramada görünür (inceleme S8)
  const expandAll = (node: LibraryNode) => {
    if (node.kind !== 'folder') return;
    expand.add(node.id);
    for (const child of node.children) expandAll(child);
  };
  const visit = (list: readonly LibraryNode[]): LibraryNode[] =>
    list.flatMap((node): LibraryNode[] => {
      if (node.kind === 'document') return has(documentText(node.document)) ? [node] : [];
      if (
        node.folder.group === undefined &&
        has(`${node.folder.code}\n${node.folder.name}\n${folderLabel(node.folder)}`)
      ) {
        expandAll(node);
        return [node];
      }
      const children = visit(node.children);
      if (children.length === 0) return [];
      expand.add(node.id);
      return [{ ...node, children }];
    });
  return { nodes: visit(nodes), expand };
}

export interface TreeRow {
  id: string;
  /** `record`: formun altındaki kayıt, ipucu ya da "All (n)" satırı (ADR 0072). */
  kind: 'folder' | 'document' | 'record';
  level: number;
  parentId: string | null;
  expandable: boolean;
}

/**
 * Belgelerin alt düzeyi (ISM ağacında formun kayıtları; ADR 0072): hangi belge açılır ve açık
 * belgenin alt satırları. Verilmezse belgeler uç düğümdür.
 */
export interface TreeChildren {
  expandable(document: LibraryDocument): boolean;
  rows(documentId: string): readonly string[];
}

/** Görünür satırlar sırasıyla (klavye gezinmesi ve `aria-level`). */
export function treeRows(
  nodes: readonly LibraryNode[],
  expanded: ReadonlySet<string>,
  children?: TreeChildren,
): TreeRow[] {
  const rows: TreeRow[] = [];
  const visit = (list: readonly LibraryNode[], level: number, parentId: string | null) => {
    for (const node of list) {
      const opens =
        node.kind === 'folder'
          ? // Boş klasör (şablon başlığı) uç düğümdür: açılmaz (ADR 0037 §6; inceleme S9)
            node.children.length > 0
          : (children?.expandable(node.document) ?? false);
      rows.push({ id: node.id, kind: node.kind, level, parentId, expandable: opens });
      if (!opens || !expanded.has(node.id)) continue;
      if (node.kind === 'folder') visit(node.children, level + 1, node.id);
      else {
        for (const id of children?.rows(node.id) ?? []) {
          rows.push({ id, kind: 'record', level: level + 1, parentId: node.id, expandable: false });
        }
      }
    }
  };
  visit(nodes, 1, null);
  return rows;
}

export type TreeAction =
  | { focus: string }
  | { toggle: string }
  | { select: string }
  /** Kayıt satırı: kaydı ya da formun kayıt listesini açar (ADR 0072). */
  | { activate: string };

/**
 * Ağaç klavyesi (WAI-ARIA "Tree View"): aşağı/yukarı bir satır, Home/End ilk ve son, sağ ok
 * kapalı düğümü (klasör ya da form) açar ya da ilk çocuğa gider, sol ok açık düğümü kapatır ya
 * da üste gider, Enter ve boşluk dokümanı seçer, kaydı açar ya da klasörü açar/kapatır.
 */
export function treeKeyAction(
  rows: readonly TreeRow[],
  focusedId: string,
  key: string,
  expanded: ReadonlySet<string>,
  /** Aramada klasörler açık kalır: sol ok üste gider, Enter/boşluk klasörde tüketilmez (R35). */
  options: { searching?: boolean } = {},
): TreeAction | null {
  const index = rows.findIndex((row) => row.id === focusedId);
  const row = rows[index];
  if (!row) return rows[0] ? { focus: rows[0].id } : null;
  const at = (i: number) => rows[Math.max(0, Math.min(rows.length - 1, i))]?.id ?? row.id;
  // Aramada yalnız klasörler zorla açıktır; form düğümü yine açılıp kapanır
  const locked = options.searching === true && row.kind === 'folder';
  switch (key) {
    case 'ArrowDown':
      return { focus: at(index + 1) };
    case 'ArrowUp':
      return { focus: at(index - 1) };
    case 'Home':
      return { focus: at(0) };
    case 'End':
      return { focus: at(rows.length - 1) };
    case 'ArrowRight': {
      if (!row.expandable) return null;
      if (!expanded.has(row.id)) return { toggle: row.id };
      const child = rows[index + 1];
      return child && child.parentId === row.id ? { focus: child.id } : null;
    }
    case 'ArrowLeft':
      if (row.expandable && expanded.has(row.id) && !locked) return { toggle: row.id };
      return row.parentId ? { focus: row.parentId } : null;
    case 'Enter':
    case ' ':
      if (row.kind === 'document') return { select: row.id };
      if (row.kind === 'record') return { activate: row.id };
      return row.expandable && !locked ? { toggle: row.id } : null;
    default:
      return null;
  }
}

/** Revizyon durumunun rozet tonu (plan Task 14; README "Tasarım değerleri"). */
const TONES: Readonly<Record<RevisionState, StatusTone>> = {
  draft: 'info',
  pending_approval: 'warning',
  approved: 'warning',
  scheduled: 'info',
  published: 'success',
  superseded: 'danger',
  withdrawn: 'danger',
  cancelled: 'danger',
  discarded: 'neutral',
};

export function revisionTone(state: RevisionState): StatusTone {
  return TONES[state];
}

/**
 * Dosya boyutu ondalık birimlerle (müşteri ekranı "2.4 MB"), dile göre ondalık ayırıcı. MB ve
 * üstünde 10'un altı bir ondalıkla.
 */
/**
 * Depo boyutu (kota, kullanılan alan, boş disk) ikili birimle: 20 GiB kota "20 GB" görünür ve
 * ayar formundaki GB ile aynıdır (ADR 0028 #31; Windows Gezgini de böyle gösterir). Bir ondalık.
 */
export function formatStorageSize(bytes: number, locale: string): string {
  const units = [
    { unit: 'GB', size: 1024 ** 3 },
    { unit: 'MB', size: 1024 ** 2 },
    { unit: 'KB', size: 1024 },
  ];
  for (const { unit, size } of units) {
    if (bytes >= size) {
      const text = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(bytes / size);
      return `${text} ${unit}`;
    }
  }
  return `${new Intl.NumberFormat(locale).format(bytes)} B`;
}

export function formatFileSize(bytes: number, locale: string): string {
  const units = [
    { unit: 'GB', size: 1e9 },
    { unit: 'MB', size: 1e6 },
    { unit: 'KB', size: 1e3 },
  ];
  for (const { unit, size } of units) {
    if (bytes >= size) {
      const value = bytes / size;
      const digits = unit !== 'KB' && value < 10 ? 1 : 0;
      const text = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      }).format(value);
      return `${text} ${unit}`;
    }
  }
  return `${new Intl.NumberFormat(locale).format(bytes)} B`;
}

/** "Updated" rozeti: gemide dosya doğrulandıktan sonraki 30 gün (ADR 0028 #39 madde 3). */
export function isUpdated(document: LibraryDocument, now: string): boolean {
  return Boolean(document.updatedUntil && document.updatedUntil > now);
}

/**
 * İndirme ve basma: gemide yalnız yürürlükteki revizyon (ADR 0032 #87) [ISM 11.2.3]; ofiste her
 * durum etiketli adla ve filigranla (#30 E).
 */
export function canOutputRevision(scope: MenuScope, state: RevisionState): boolean {
  return scope === 'web' || state === 'published';
}

/**
 * "Ne değişti" şeridinin ve teyidin revizyonu (inceleme M8a3-R6, R23): teyit bekleyen, yoksa
 * yürürlükteki; History'den önizlenen eski revizyon değil. Yürürlükte revizyon yoksa (geri çekme)
 * önizlenen.
 */
export function changesRevision(
  detail: LibraryDetail,
  fallback: LibraryRevision | null,
): LibraryRevision | null {
  const id = detail.acknowledgementDue?.revisionId ?? detail.current?.revisionId;
  return detail.revisions.find((r) => r.id === id) ?? fallback;
}

/**
 * Okumaların sıra denetimi (inceleme M8a3-R8): gemide SSE ve senkron turu aynı anda okuma
 * başlatır; geç dönen eski yanıt yeni seçimin ya da yeni ağacın yerine geçmez. Her okuma bir
 * numara alır, yanıt yalnız son numaraysa uygulanır.
 */
export function createRequestGate(): { next(): number; isLatest(ticket: number): boolean } {
  let last = 0;
  return {
    next: () => {
      last += 1;
      return last;
    },
    isLatest: (ticket) => ticket === last,
  };
}

/** Gün dönümünden sonra kaç dakika daha her dakika yeniden okunur (inceleme M8a3-R16). */
export const DAY_ROLLOVER_GRACE_TICKS = 5;

/**
 * Gemide dakikalık zamanlayıcının bir adımı (inceleme WF-1, M8a3-R16): gemi yerel günü dönünce
 * ileri tarihli revizyon yürürlüğe girer (ADR 0028 #27). Gün PC saatiyle izlenir, durumu ise gemi
 * sunucusu kendi saatiyle türetir; internetsiz gemide saatler dakikalarca farklı olabilir. Bu
 * yüzden gün değişince ve sonraki beş dakika her dakika yeniden okunur.
 */
export function dayRolloverTick(
  state: { day: string; graceLeft: number },
  today: string,
): { state: { day: string; graceLeft: number }; reload: boolean } {
  if (today !== state.day) {
    return { state: { day: today, graceLeft: DAY_ROLLOVER_GRACE_TICKS }, reload: true };
  }
  if (state.graceLeft > 0)
    return { state: { day: today, graceLeft: state.graceLeft - 1 }, reload: true };
  return { state, reload: false };
}

/** Download ve Print'in pasif nedeni (inceleme M8a3-R12, R13); `null`: etkin. */
export type OutputBlock = 'noRevision' | 'notCurrent' | 'notOnBoard';

/**
 * Önizlenen revizyonun çıktısı neden pasif: revizyon yok; gemide yürürlükte değil (detay ile dosya
 * ucunun durumundan sıkı olan; ADR 0032 #87); gemide dosya henüz doğrulanmadı (önizleyici yok,
 * basılacak sayfa yok). Ofiste yürürlükte olmayan revizyon sorularak indirilir (#30 E).
 */
export function outputBlock(
  scope: MenuScope,
  state: RevisionState | null,
  served: RevisionState | null,
  missing: boolean,
): OutputBlock | null {
  if (state === null) return 'noRevision';
  if (!canOutputRevision(scope, strictestState(state, served))) return 'notCurrent';
  if (scope === 'ship' && missing) return 'notOnBoard';
  return null;
}

/**
 * Ekran veri bekliyor mu (inceleme M8a3-R5): iskelet yalnız o zaman. Boş kütüphanede, ağaç ya da
 * detay hatasında iskelet çizilmez. Ağaçta doküman varken seçim bir sonraki çizimde gelir; o an
 * da yükleniyor sayılır (boş başlık yanıp sönmez).
 */
export function libraryLoading(
  tree: 'loading' | 'ready' | 'error',
  documents: number,
  detail: 'idle' | 'loading' | 'ready' | 'error',
): boolean {
  if (tree === 'loading' || detail === 'loading') return true;
  return tree === 'ready' && documents > 0 && detail === 'idle';
}

/** Dosya ucundan okunan durum ve okunduğu anda detayın söylediği durum (inceleme M8a3-R14). */
export interface ServedObservation {
  revisionId: string;
  state: RevisionState | null;
  detailState: RevisionState;
}

/**
 * Dosya ucunun gözlemi yalnız gözlendiği detayla geçerlidir (inceleme M8a3-R14): detay yeniden
 * okunup revizyonun durumu değişince (gün dönümünde zamanlanmış revizyon yürürlüğe girer) eski
 * gözlem düşer, detay geçerlidir. Detay bayatken dosya ucunun söylediği daha sıkı durum (WF-1)
 * detay değişene kadar geçerli kalır.
 */
export function observedState(
  revision: { id: string; state: RevisionState },
  observation: ServedObservation | null,
): RevisionState | null {
  if (!observation || observation.revisionId !== revision.id) return null;
  return observation.detailState === revision.state ? observation.state : null;
}

/** Dosya ucunun `x-felix-revision-state` başlığı: bilinen durumsa o, değilse `null`. */
export function servedRevisionState(header: string | null | undefined): RevisionState | null {
  const value = header?.trim() ?? '';
  return (REVISION_STATES as readonly string[]).includes(value) ? (value as RevisionState) : null;
}

/**
 * Detaydaki durumla dosya ucunun bildirdiği durumdan daha sıkı olanı (inceleme WF-1; ADR 0032
 * #87): biri yürürlükte değilse filigran ve indirme/basma kısıtı ondan. Detay bayat kalabilir
 * (senkron turu); dosya ucu o anki durumu söyler [ISM 11.2.3].
 */
export function strictestState(
  detail: RevisionState,
  served: RevisionState | null | undefined,
): RevisionState {
  return served && served !== 'published' ? served : detail;
}

/** Gemi yerel günü (`YYYY-MM-DD`); saat dilimi yoksa UTC günü (ileri tarihli yürürlük, #27). */
export function vesselDay(timeZone: ShipTime | null, at: string | Date): string {
  const time = at instanceof Date ? at.getTime() : Date.parse(at);
  const offset = timeZone ? vesselOffsetMinutes(timeZone, new Date(time)) : 0;
  return new Date(time + offset * 60_000).toISOString().slice(0, 10);
}

/**
 * Gemide revizyonu "henüz gemide değil" yapan dosya (inceleme F2): revizyon ana dosya ve baskı
 * PDF'i doğrulanınca gemidedir (ADR 0032); önce ana dosya, sonra baskı PDF'i. Kart, kuyruk
 * sırası ve "Download now" bu dosya içindir. Dosya durumu bilinmiyorsa ana dosya.
 */
export function missingRevisionFile(
  revision: LibraryRevision,
): { fileId: string; size: number } | null {
  const files = revision.files ?? [];
  const pending = (role: 'main' | 'print_rendition') =>
    files.find((f) => f.role === role && f.state !== 'verified');
  const main = pending('main');
  if (main || files.length === 0) return { fileId: revision.file.id, size: revision.file.size };
  const print = pending('print_rendition');
  if (!print) return null;
  const attachment = revision.attachments.find((a) => a.id === print.fileId);
  return { fileId: print.fileId, size: attachment?.size ?? 0 };
}

/**
 * İndirilen ana dosyanın adı (ADR 0028 #30 C, E): "Form 01 - Safety Management System (Rev 1.0)
 * .docx"; yürürlükte değilse durum etiketli. Sunucunun `Content-Disposition`'ıyla aynı kural;
 * gemide kaydetme penceresinin önerdiği ad (inceleme F3).
 */
export function mainDownloadName(
  document: Pick<LibraryDocument, 'kind' | 'docNo' | 'title'>,
  revision: Pick<LibraryRevision, 'revisionNo' | 'state' | 'file'>,
): string {
  return downloadFileName({
    kind: document.kind,
    docNo: document.docNo,
    title: document.title,
    revisionNo: revision.revisionNo,
    fileName: revision.file.name,
    state: revision.state,
  });
}

/** Önizleme filigranı: yürürlükte olmayan revizyonun durumu (dosya adındaki etiketle aynı). */
export function watermarkText(state: RevisionState): string | null {
  return revisionStateLabel(state);
}

/**
 * Önizlenen revizyon: adresteki (`?rev=`), yoksa yürürlükteki, o da yoksa ana dalın son
 * revizyonu (geri çekilmiş, zamanlanmış ya da ofiste taslak).
 */
export function previewRevision(
  detail: LibraryDetail,
  revisionId: string | null | undefined,
): LibraryRevision | null {
  const byId = (id: string | null | undefined) =>
    id ? detail.revisions.find((r) => r.id === id) : undefined;
  return (
    byId(revisionId) ??
    byId(detail.current?.revisionId) ??
    byId(detail.scheduled?.revisionId) ??
    [...detail.revisions].sort((a, b) => b.seqNo - a.seqNo)[0] ??
    null
  );
}

/**
 * Geminin saat diliminin o andaki UTC farkı (dakika; altlık ve "Last Updated" gemi saatiyle).
 * Bilinmeyen bölge 0 (UTC) olur; ekran düşmez.
 */
export function vesselOffsetMinutes(timeZone: ShipTime, at: string | Date): number {
  try {
    return zoneOffsetMinutes(timeZone, (at instanceof Date ? at : new Date(at)).getTime());
  } catch {
    return 0;
  }
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * "Rev. 2": ekranda ve PDF'te revizyonun tek gösterimi (ADR 0107 §3). Serbest metin numaranın
 * öneki ("Rev", "Rev.", "Revision", sayıdan önce "v") atılır; "v2" ile "Rev. 2" aynı görünür.
 */
export function revisionText(revisionNo: string): string {
  return `Rev. ${revisionBare(revisionNo)}`;
}

/** Ağaç satırındaki kısa revizyon: "Rev.3" (ortağın geri bildirimi 2026-10-17). */
export function revisionShort(revisionNo: string): string {
  return `Rev.${revisionBare(revisionNo)}`;
}

/**
 * Revizyon no'su öneksiz: metin anahtarları "Rev. {{revision}}" yazar; serbest metin no'su
 * "Rev. 5" ise "Rev. Rev. 5" çıkmaz (inceleme M8a3-R24); "v2" ve "Revision 2" de "2" olur
 * (ADR 0107 §3).
 */
export function revisionBare(revisionNo: string): string {
  return revisionNo
    .trim()
    .replace(/^rev(ision)?\.?\s*/i, '')
    .replace(/^v(?=\d)/i, '');
}

/**
 * Sonraki revizyon numarası (ADR 0107 §3): dokümanın revizyonlarının en büyük tam sayı
 * numarasından bir sonraki; revizyon yoksa "0" (Original). Numaralardan biri tam sayı değilse
 * (serbest metin, ör. "1.0") `null`: kullanıcı numarayı kendisi yazar.
 */
export function nextRevisionNo(revisionNos: readonly string[]): string | null {
  let max = -1;
  for (const raw of revisionNos) {
    const bare = revisionBare(raw);
    if (!/^\d+$/.test(bare)) return null;
    max = Math.max(max, Number(bare));
  }
  return String(max + 1);
}

/** Revizyon seçimi listesi: kaç numara gösterilir. */
const REVISION_CHOICE_COUNT = 30;

/**
 * Revizyon seçimi (ADR 0107 §3): "Original (Rev. 0)", "Rev. 1", …; `from`'dan başlar. Şirketin
 * kâğıtta revize ettiği form sisteme o revizyonla girer; sonrakiler bundan devam eder.
 */
export function revisionChoices(
  from: number,
  t: (key: string, values?: Record<string, unknown>) => string,
): { value: string; label: string }[] {
  return Array.from({ length: REVISION_CHOICE_COUNT }, (_, i) => from + i).map((no) => ({
    value: String(no),
    label: t(no === 0 ? 'documents.revisionChoice.original' : 'documents.revisionChoice.rev', {
      no,
    }),
  }));
}

/** "Ahmet Kaya" → "A. Kaya". */
function shortName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName.trim();
  return `${parts[0]?.charAt(0)}. ${parts.at(-1)}`;
}

export interface PrintFooterInput {
  kind: LibraryDocument['kind'];
  docNo: string;
  revisionNo: string;
  /** Yürürlük günü (`YYYY-MM-DD`); yoksa altlıkta yazmaz. */
  effectiveOn: string | null;
  printedAt: string;
  /** Gemide gemi saati farkı; ofiste `null` (UTC). */
  offsetMin: number | null;
  printedBy: string;
  /** Gemi adı ya da "Office". */
  place: string;
  contentHash: string;
  state: RevisionState;
  /** Dalda yürürlükteki revizyon; yoksa `null`. */
  currentRevisionNo: string | null;
  /** Yürürlükte revizyon yoksa geri çekilme günü. */
  withdrawnOn: string | null;
  /** Reinstate ile açıldıysa içeriğin kaynağı. */
  reinstatedFromRevisionNo: string | null;
  /**
   * Print Form'un kayıt numarası (ADR 0042 §20): taranan kâğıt kayda bağlanır ("Record FR-…").
   * Başlıktaki Print boş kopya basar; numara yoktur.
   */
  recordNo?: string | null;
}

/** Çevirici: i18next `t` ya da testte basit bir eşleme. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Basılı kopyanın her sayfasındaki altlık (ADR 0028 #30 A, E): kontrolsüz kopya ibaresi,
 * doküman ve revizyon, yürürlük, basım zamanı (gemide gemi saati ve farkı, ofiste UTC), basan,
 * yer ve revizyonun içerik hash'inin ilk 12 hanesi. Yürürlükte olmayan revizyonda "NOT FOR
 * OPERATIONAL USE" ve güncel revizyon. Altlık İngilizcedir; metinler i18n anahtarlarındadır.
 */
export function printFooter(t: Translate, input: PrintFooterInput): string {
  const at = new Date(new Date(input.printedAt).getTime() + (input.offsetMin ?? 0) * 60_000);
  const stamp = `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())} ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`;
  let zone = 'UTC';
  if (input.offsetMin !== null) {
    const sign = input.offsetMin < 0 ? '-' : '+';
    const abs = Math.abs(input.offsetMin);
    zone = `LT (UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)})`;
  }
  const parts = [
    ...(input.recordNo ? [t('forms.print.record', { record: input.recordNo })] : []),
    input.state === 'published'
      ? t('documents.printFooter.uncontrolled')
      : t('documents.printFooter.reference', {
          current: input.currentRevisionNo
            ? revisionText(input.currentRevisionNo)
            : t('documents.printFooter.none', { date: input.withdrawnOn ?? '—' }),
        }),
    input.kind === 'form' ? `Form ${input.docNo}` : input.docNo,
    revisionText(input.revisionNo),
  ];
  if (input.reinstatedFromRevisionNo) {
    parts.push(t('documents.printFooter.contentOf', { revision: input.reinstatedFromRevisionNo }));
  }
  if (input.effectiveOn) {
    parts.push(t('documents.printFooter.effective', { date: input.effectiveOn }));
  }
  parts.push(
    t('documents.printFooter.printed', {
      at: `${stamp} ${zone}`,
      by: shortName(input.printedBy),
    }),
    input.place,
    t('documents.printFooter.hash', { hash: input.contentHash.slice(0, 12) }),
  );
  return parts.join(' · ');
}
